/* eslint-disable no-console */
import TransactionModal from "@components/TransactionModal";
import {
  CLAIM_DISCLAIMER_APPROVED,
  CLAIM_DISCLAIMER_REJECT,
  CLAIM_DISCLAIMER_VIEWED,
  CLAIM_EL_FAILED,
  CLAIM_EL_SUCCESSFUL,
  CLAIM_SUCCESSFUL,
  CLAIM_FAILED,
} from "@constants/analytics";
import { formatEtherNumber, TXN_TYPES } from "@constants/common";
import useClaims, { Airdrop, ClaimData, ClaimType } from "@hooks/useClaims";
import useDappChain from "@hooks/useDappChain";
import useSanction from "@hooks/useSanction";
import { Typography } from "@kelp-dao/kernel-design-system";
import { isKernelAirdropClaim } from "@common/utils";
import airdrops from "@data/airdrops.json";
import Link from "next/link";
import { useRouter } from "next/router";
import { useEffect, useState } from "react";
import { isErrorRejectedByUser, trackEvent } from "utils";
import { Abi, Hash } from "viem";
import { useAccount, usePublicClient, useWriteContract } from "wagmi";
import ActiveAirDrops from "./ActiveAirdrops";
import Disclaimer from "./Disclaimer";
import { EigenDisclaimer } from "./EigenDisclaimer";
import { KernelDisclaimer } from "./KernelDisclaimer";

const getTransactionInitialState = () => ({
  open: false,
  hash: "",
  error: "",
  hasUserDenied: false,
  amount: "",
});

const ClaimEL = () => {
  const { chain } = useDappChain();
  const { address } = useAccount();
  const publicClient = usePublicClient();
  const { writeContract } = useWriteContract();
  const router = useRouter();
  const [hasSecretKeyForClaim, setHasSecretKeyForClaim] = useState(false);

  useEffect(() => {
    const { secret_key } = router.query;
    setHasSecretKeyForClaim(secret_key === "claimAllowed");
  }, [router.query]);

  const [isTxModalOpen, setIsTxModalOpen] = useState(
    getTransactionInitialState()
  );

  const [pendingTx, setPendingTx] = useState(false);

  const { isLoading, data } = useClaims();

  const [isDisclaimerModalOpen, setIsDisclaimerModalOpen] = useState(false);
  const [claimType, setClaimType] = useState<ClaimType>();

  const handleClaim = (data: Airdrop) => {
    setIsDisclaimerModalOpen(true);
    setClaimType(data.type);

    // Track disclaimer viewed
    trackEvent(CLAIM_DISCLAIMER_VIEWED, {
      chain_id: String(chain?.id || ""),
    });
  };

  const onClaimSubmit = () => {
    if (isLoading || !publicClient) return;

    const airdrop = data.find((d) => d.data?.type === claimType);

    if (!airdrop?.data) {
      return;
    }

    setIsTxModalOpen({
      ...isTxModalOpen,
      open: true,
    });
    setPendingTx(true);

    const shouldUseClaimFunction =
      hasSecretKeyForClaim && isKernelAirdropClaim(claimType as ClaimType);

    const functionName = shouldUseClaimFunction
      ? "claim"
      : isKernelAirdropClaim(airdrop.data.type as ClaimType)
      ? "claimAndStake"
      : "claim";

    console.log({
      abi: airdrop.data.contract?.abi,
      address: airdrop.data.contract?.address,
      functionName,
      args: [
        airdrop?.data.currentIndex,
        address,
        airdrop.data.totalAmount,
        airdrop.data.proofs,
      ],
    });

    const args = !isKernelAirdropClaim(airdrop.data.type as ClaimType)
      ? [
          airdrop?.data.currentIndex,
          address,
          airdrop.data.totalAmount,
          airdrop.data.proofs,
        ]
      : [airdrop?.data.totalAmount, airdrop?.data.proofs];

    console.log("args", args);

    writeContract(
      {
        address: airdrop.data.contract?.address as `0x${string}`,
        abi: airdrop.data.contract?.abi as Abi,
        functionName,
        args: args,
      },
      {
        onError: (error) => {
          console.log({ error });

          // @ts-ignore
          const errorMessage = error.shortMessage || error.message;

          setIsTxModalOpen({
            open: true,
            // @ts-ignore
            hash: error.transactionHash,
            error: errorMessage,
            hasUserDenied: isErrorRejectedByUser(error),

            amount: airdrop?.data
              ? formatEtherNumber(
                  airdrop.data.availableAmountToClaim?.toString() ||
                    airdrop.data.availableAmount?.toString() ||
                    "0"
                ).toString()
              : "",
          });
          trackEvent(CLAIM_EL_FAILED, {
            error: errorMessage,
            hasUserDenied: isErrorRejectedByUser(error),
            amount: airdrop.data?.availableAmount,
          });

          // Track generic claim failed
          trackEvent(CLAIM_FAILED, {
            Dapp: "Kelp",
            txn_hash: null,
            chain: String(chain?.id) || "",
            error_reason: isErrorRejectedByUser(error)
              ? "User rejected"
              : errorMessage,
          });

          setPendingTx(false);
        },
        onSuccess: async (hash: Hash) => {
          await publicClient?.waitForTransactionReceipt({
            hash,
          });

          airdrop.refetch();

          setPendingTx(false);
          trackEvent(CLAIM_EL_SUCCESSFUL, {
            token_out_amount: airdrop.data?.availableAmount,
            hash,
            token_out_symbol: airdrop.data?.unit,
          });

          // Track generic claim successful
          trackEvent(CLAIM_SUCCESSFUL, {
            Dapp: "Kelp",
            txn_hash: hash,
            chain: String(chain?.id) || "",
          });

          setIsTxModalOpen({
            open: true,
            hash,
            error: "",
            hasUserDenied: false,
            amount: airdrop?.data
              ? formatEtherNumber(
                  airdrop.data.availableAmountToClaim?.toString() ||
                    airdrop.data.availableAmount?.toString() ||
                    "0"
                ).toString()
              : "",
          });
        },
      }
    );
  };

  const claimedAirdrops = data.filter(
    (d) => (d.data?.claimedAmount || 0n) > 0n
  );
  const { isSanctioned } = useSanction();
  return (
    <>
      <div
        className={`flex flex-col gap-6 max-w-full md:max-w-[440px] lg:max-w-[768px] xl:max-w-[1100px] mx-auto mt-0 md:mt-6 lg:mt-0 ${
          isSanctioned
            ? "pointer-events-none opacity-50"
            : "pointer-events-auto opacity-100"
        }`}
      >
        <div className="flex flex-col gap-1">
          <Typography
            as="h1"
            type="heading"
            variant="default"
            className="text-text-primary text-xl font-medium leading-7 lg:text-2xl lg:font-semibold lg:leading-8"
          >
            Claims
          </Typography>
          <Typography
            as="p"
            type="body"
            variant="xsmall"
            className="text-text-secondary"
          >
            Claim your airdrop quickly and securely. For more information, you
            can read the FAQ{" "}
            <Link
              href="https://kerneldao.gitbook.io/kernel/getting-started/gain/claim-tokens"
              className="underline"
              target="_blank"
            >
              here
            </Link>
            .
          </Typography>
        </div>
        <ActiveAirDrops
          airdrops={data as ClaimData[]}
          handleClaim={handleClaim}
          isLoading={isLoading}
          hasSecretKeyForClaim={hasSecretKeyForClaim}
          futureAirdrops={airdrops.futureAirdrops}
          claimedAirdrops={claimedAirdrops as ClaimData[]}
        />
      </div>

      {isDisclaimerModalOpen && claimType && (
        <Disclaimer
          isOpen={isDisclaimerModalOpen}
          onClose={() => {
            trackEvent(CLAIM_DISCLAIMER_REJECT);
            setIsDisclaimerModalOpen(false);
          }}
          onApprove={() => {
            trackEvent(CLAIM_DISCLAIMER_APPROVED, {
              chain_id: String(chain?.id || ""),
            });
            onClaimSubmit();
            setIsDisclaimerModalOpen(false);
          }}
          type={claimType}
          {...(isKernelAirdropClaim(claimType as ClaimType) && {
            primaryBtnTxt: "Continue",
          })}
          disclaimer={
            isKernelAirdropClaim(claimType as ClaimType) ? (
              <KernelDisclaimer hasSecretKeyForClaim={hasSecretKeyForClaim} />
            ) : (
              <EigenDisclaimer />
            )
          }
        />
      )}

      <TransactionModal
        isOpen={isTxModalOpen.open}
        hash={isTxModalOpen.hash}
        error={isTxModalOpen.error}
        transactionType={
          isKernelAirdropClaim(claimType as ClaimType)
            ? TXN_TYPES.CLAIM_KERNEL_AIRDROPS
            : TXN_TYPES.CLAIM_EL_POINTS
        }
        isTxnProcessing={pendingTx}
        hasUserDenied={isTxModalOpen.hasUserDenied}
        closeAlert={() => setIsTxModalOpen(getTransactionInitialState())}
        handleTxView={() => {
          const url = `${chain.blockExplorers?.default.url}/tx/${isTxModalOpen.hash}`;
          window.open(url, "_blank");
        }}
        chainId={chain.id}
        amount={isTxModalOpen.amount}
        hasSecretKeyForClaim={hasSecretKeyForClaim}
      />
    </>
  );
};

export default ClaimEL;
